import { DIVIDED, INSET, MUTED, PRIMARY, ROW, Dot, Flow, Panel, Photo, Pill, type Tone } from './SceneKit';
import { SAMPLE_FIGURES as F, dayCount, usd } from '../../data/sample-figures';

/*
 * Simplified, illustrative scenes of the Resida app, one per feature page. They show the key moment
 * of each feature with plausible sample data, using only states and terms the app really has. They
 * are built from markup rather than screenshots so they stay crisp and are easy to keep in sync as
 * the app changes.
 */

function RentScene(): JSX.Element {
  const payments: { date: string; memo: string; amount: string; tone: Tone; result: string }[] = [
    { date: 'Oct 1', memo: 'ZELLE FROM JORDAN SMITH', amount: usd(F.rentJordan), tone: 'good', result: 'Paid · 12 Oak St, Unit 2' },
    { date: 'Oct 1', memo: 'VENMO *PRIYA PATEL', amount: usd(F.rentPriya), tone: 'good', result: 'Paid · 48 Elm Ct' },
    { date: 'Oct 2', memo: 'ONLINE TRANSFER M LEE', amount: usd(F.rentMaplePartial), tone: 'warn', result: 'Partial · Maple Ave, Unit B' },
  ];
  return (
    <div>
      <Panel title="Bank feed" meta="Rent checking ••4821">
        <ul className={DIVIDED}>
          {payments.map((p) => (
            <li key={p.memo} className={ROW}>
              <div className="min-w-0">
                <div className={`truncate font-mono text-xs ${PRIMARY}`}>{p.memo}</div>
                <div className="mt-1"><Pill tone={p.tone}>{p.result}</Pill></div>
              </div>
              <div className="text-right">
                <div className={`font-semibold ${PRIMARY}`}>{p.amount}</div>
                <div className={MUTED}>{p.date}</div>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
      <Flow label="No payment from 12 Oak St, Unit 1" />
      <Panel title="12 Oak St, Unit 1" meta="Rent due Oct 1">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className={PRIMARY}>{usd(F.rentOwed)} not received</span>
          <Pill tone="bad">Late · {dayCount(F.lateDays)}</Pill>
        </div>
        <div className={`mt-3 flex items-center justify-between gap-3 px-3 py-2 text-xs ${INSET}`}>
          <span className="text-[var(--landing-text-secondary)]">Late fee per your lease</span>
          <span className={`font-semibold ${PRIMARY}`}>{usd(F.lateFee)} · Approve?</span>
        </div>
      </Panel>
    </div>
  );
}

function LeasingScene(): JSX.Element {
  const stages = [
    { name: 'Inquiring', count: 6 },
    { name: 'Showing', count: 2 },
    { name: 'Applying', count: 1 },
  ];
  return (
    <div>
      <Panel title="Listing · Maple Ave, Unit A" meta={`3 bd · 2 ba · ${usd(F.listingRent)}`}>
        <div className="flex gap-3">
          <Photo className="h-14 w-20 flex-shrink-0" />
          <div className="min-w-0 space-y-1.5">
            <div className={`text-sm ${PRIMARY}`}>Sunny 3-bed near the park</div>
            <div className="flex flex-wrap gap-1.5">
              <Pill tone="good">Craigslist · posted</Pill>
              <Pill tone="good">Facebook Marketplace · posted</Pill>
            </div>
          </div>
        </div>
      </Panel>
      <Flow label="Every inquiry lands in one pipeline" />
      <Panel title="Leads">
        <div className="grid grid-cols-3 gap-2">
          {stages.map((s) => (
            <div key={s.name} className={`px-2 py-2 text-center ${INSET}`}>
              <div className={`text-lg font-semibold ${PRIMARY}`}>{s.count}</div>
              <div className={MUTED}>{s.name}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2 text-xs">
          <div className="w-fit max-w-[85%] rounded-lg bg-[var(--landing-surface-alt)] px-3 py-2 text-[var(--landing-text-secondary)]">
            Is it still available? Are dogs OK?
          </div>
          <div className="ml-auto w-fit max-w-[85%] rounded-lg bg-[var(--landing-accent)]/20 px-3 py-2 text-[var(--landing-text-primary)]">
            Yes! Dogs under 40 lb are welcome. Here is a link to pick a showing time.
            <div className="mt-1 text-[10px] text-[var(--landing-accent-light)]">Leasing assistant</div>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function MaintenanceScene(): JSX.Element {
  const timeline: { tone: Tone; text: string; meta: string }[] = [
    { tone: 'info', text: 'Triaged as urgent: active leak', meta: '9:02 pm' },
    { tone: 'info', text: 'Texted Ace Plumbing from your vendor list', meta: '9:03 pm' },
    { tone: 'good', text: 'Visit booked for Thu 10:00 am', meta: '9:41 pm' },
    { tone: 'good', text: 'Jordan notified of the visit', meta: '9:42 pm' },
  ];
  return (
    <div>
      <Panel title="Repair request · 12 Oak St, Unit 2" meta="from Jordan">
        <div className="flex gap-3">
          <Photo className="h-14 w-14 flex-shrink-0" />
          <div className="min-w-0">
            <div className={`text-sm ${PRIMARY}`}>Kitchen sink leaking under the cabinet</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <Pill tone="bad">Urgent</Pill>
              <Pill tone="neutral">2 photos</Pill>
            </div>
          </div>
        </div>
      </Panel>
      <Flow label="Repair coordinator" />
      <Panel title="Activity" meta="In progress">
        <ul className="space-y-2.5">
          {timeline.map((t) => (
            <li key={t.text} className="flex items-start gap-3 text-sm">
              <Dot tone={t.tone} />
              <span className="min-w-0 flex-1 text-[var(--landing-text-secondary)]">{t.text}</span>
              <span className={`flex-shrink-0 ${MUTED}`}>{t.meta}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function ExpenseScene(): JSX.Element {
  const fields: [string, string][] = [
    ['Vendor', 'Ace Plumbing'],
    ['Amount', usd(F.invoice, true)],
    ['Date', 'Oct 10'],
    ['Unit', '12 Oak St, Unit 2'],
    ['Schedule E', 'Line 14 · Repairs'],
  ];
  return (
    <div>
      <Panel title="Fwd: Invoice #1182" meta="to expenses@resida.me">
        <div className={`flex items-center gap-3 px-3 py-2 ${INSET}`}>
          <span className={`text-xs font-semibold ${PRIMARY}`}>PDF</span>
          <span className="truncate text-xs text-[var(--landing-text-secondary)]">ace-plumbing-invoice-1182.pdf</span>
        </div>
      </Panel>
      <Flow label="Details read and matched to the unit" />
      <Panel title="Expense" meta="Filed automatically">
        <dl className={`${DIVIDED} text-sm`}>
          {fields.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 py-1.5">
              <dt className={MUTED}>{label}</dt>
              <dd className={`text-right ${PRIMARY}`}>{value}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Pill tone="good">Matched: Ace fixed this sink on Oct 9</Pill>
          <Pill tone="neutral">Original email kept</Pill>
        </div>
      </Panel>
    </div>
  );
}

function AiAssistantsScene(): JSX.Element {
  return (
    <Panel title="Your AI assistant" meta="Resida connected">
      <div className="space-y-3 text-sm">
        <div className="ml-auto w-fit max-w-[85%] rounded-lg bg-[var(--landing-surface-alt)] px-3 py-2 text-[var(--landing-text-primary)]">
          Who&apos;s behind on rent this month?
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--landing-text-muted)]">
          <span className="rounded border border-[var(--landing-border)] px-1.5 py-0.5 font-mono">resida · list_overdue_tenants</span>
        </div>
        <div className="max-w-[92%] space-y-2 text-[var(--landing-text-secondary)]">
          <p>Two tenants are behind:</p>
          <ul className="space-y-1.5">
            <li className="flex items-center justify-between gap-3">
              <span>12 Oak St, Unit 1</span>
              <Pill tone="bad">{usd(F.rentOwed)} · {dayCount(F.lateDays)} late</Pill>
            </li>
            <li className="flex items-center justify-between gap-3">
              <span>Maple Ave, Unit B</span>
              <Pill tone="warn">{usd(F.rentMaplePartial)} still owed</Pill>
            </li>
          </ul>
          <p>Unit 1 has a {usd(F.lateFee)} late fee waiting for your approval. Want me to approve it?</p>
        </div>
      </div>
    </Panel>
  );
}

function DashboardScene(): JSX.Element {
  const items: { tone: Tone; title: string; detail: string }[] = [
    { tone: 'bad', title: 'Rent past due', detail: `12 Oak St, Unit 1 · ${dayCount(F.lateDays)}` },
    { tone: 'bad', title: 'Urgent repair request', detail: '12 Oak St, Unit 2 · sink leak' },
    { tone: 'warn', title: 'Late fee to approve', detail: `12 Oak St, Unit 1 · ${usd(F.lateFee)}` },
    { tone: 'warn', title: `Lease ends in ${dayCount(F.leaseEndsInDays)}`, detail: '48 Elm Ct · renew or list' },
    { tone: 'info', title: 'Lead waiting on a reply', detail: 'Maple Ave, Unit A · since yesterday' },
    { tone: 'neutral', title: 'Receipt to review', detail: `Home Depot · ${usd(F.receipt, true)}` },
  ];
  return (
    <Panel title="Needs your attention" meta="6 items">
      <ul className={DIVIDED}>
        {items.map((item) => (
          <li key={item.title} className="flex items-start gap-3 py-2.5">
            <Dot tone={item.tone} />
            <div className="min-w-0 flex-1">
              <div className={`text-sm ${PRIMARY}`}>{item.title}</div>
              <div className={`truncate ${MUTED}`}>{item.detail}</div>
            </div>
            <span className="text-[var(--landing-text-muted)]" aria-hidden="true">&rsaquo;</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function LeaseScene(): JSX.Element {
  const steps = ['Unit', 'Tenants', 'Term', 'Rent', 'Deposit', 'Pets', 'Rules', 'Disclosures', 'Review'];
  const current = 7;
  return (
    <Panel title="New lease · 48 Elm Ct" meta={`Step ${current + 1} of ${steps.length}`}>
      <div className="mb-4 flex gap-1">
        {steps.map((s, i) => (
          <span key={s} className={`h-1.5 flex-1 rounded-full ${i <= current ? 'bg-[var(--landing-accent)]' : 'bg-[var(--landing-border)]'}`} />
        ))}
      </div>
      <div className={`mb-3 text-sm font-semibold ${PRIMARY}`}>Disclosures</div>
      <ul className="space-y-2 text-sm">
        {['Lead-based paint (built before 1978)', 'Security deposit receipt and holding account', 'Move-in condition checklist'].map((d) => (
          <li key={d} className="flex items-center gap-2 text-[var(--landing-text-secondary)]">
            <span className="text-emerald-400">&#10003;</span>
            {d}
          </li>
        ))}
      </ul>
      <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
        <div className="font-semibold">Reminder for this unit&apos;s state and city</div>
        Leases here usually include a smoke and CO detector notice. Reminders only, not legal advice.
      </div>
    </Panel>
  );
}

function WalkthroughScene(): JSX.Element {
  const items: { name: string; moveIn: Tone; moveOut: Tone; outLabel: string; deduction?: string }[] = [
    { name: 'Living room walls', moveIn: 'good', moveOut: 'bad', outLabel: 'Damage', deduction: usd(F.wallDeduction) },
    { name: 'Carpet', moveIn: 'good', moveOut: 'warn', outLabel: 'Stain', deduction: usd(F.carpetDeduction) },
    { name: 'Kitchen faucet', moveIn: 'good', moveOut: 'good', outLabel: 'OK' },
  ];
  return (
    <Panel title="Deduction report · Maple Ave, Unit A" meta="Move-in vs. move-out">
      <div className="mb-3 grid grid-cols-2 gap-2">
        {['Move-in', 'Move-out'].map((phase) => (
          <div key={phase}>
            <Photo className="aspect-[4/3]" />
            <div className={`mt-1 ${MUTED}`}>{phase} · Living room</div>
          </div>
        ))}
      </div>
      <ul className={`${DIVIDED} text-sm`}>
        {items.map((item) => (
          <li key={item.name} className="flex items-center justify-between gap-2 py-2">
            <span className="min-w-0 truncate text-[var(--landing-text-secondary)]">{item.name}</span>
            <span className="flex flex-shrink-0 items-center gap-1.5">
              <Pill tone={item.moveIn}>OK</Pill>
              <span className="text-[var(--landing-text-muted)]">&rarr;</span>
              <Pill tone={item.moveOut}>{item.outLabel}</Pill>
              <span className={`w-12 text-right ${PRIMARY}`}>{item.deduction ?? '—'}</span>
            </span>
          </li>
        ))}
      </ul>
      <div className={`mt-2 flex justify-between border-t border-[var(--landing-border)] pt-2 text-sm font-semibold ${PRIMARY}`}>
        <span>Total deductions</span>
        <span>{usd(F.wallDeduction + F.carpetDeduction)}</span>
      </div>
    </Panel>
  );
}

function SigningScene(): JSX.Element {
  const states = ['Sent', 'Viewed', 'Signed'];
  return (
    <Panel title="Lease · 48 Elm Ct" meta="Signing">
      <div className={`space-y-2 p-4 ${INSET}`}>
        {[100, 92, 96, 70].map((w, i) => (
          <div key={i} className="h-1.5 rounded-full bg-[var(--landing-border)]" style={{ width: `${w}%` }} />
        ))}
        <div className="grid grid-cols-2 gap-4 pt-4">
          <div>
            <div className="h-6 border-b border-[var(--landing-text-muted)] font-serif text-sm italic text-[var(--landing-text-primary)]">Priya Patel</div>
            <div className={`mt-1 ${MUTED}`}>Tenant</div>
          </div>
          <div>
            <div className="h-6 border-b border-dashed border-[var(--landing-accent)]" />
            <div className={`mt-1 ${MUTED}`}>Landlord · your turn</div>
          </div>
        </div>
      </div>
      <ol className="mt-4 flex items-center gap-2">
        {states.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            {i > 0 && <span className="h-px w-5 bg-[var(--landing-border)]" />}
            <Pill tone={i < 2 ? 'good' : 'info'}>{i < 2 ? `${s} ✓` : `${s} · 1 of 2`}</Pill>
          </li>
        ))}
      </ol>
      <div className={`mt-3 ${MUTED}`}>No fee per document</div>
    </Panel>
  );
}

function CommunicationScene(): JSX.Element {
  const threads: { name: string; place: string; channel: string; preview: string; unread?: boolean }[] = [
    { name: 'Jordan Smith', place: '12 Oak St, Unit 2', channel: 'Chat', preview: 'Thanks, the plumber is here now.', unread: true },
    { name: 'Priya Patel', place: '48 Elm Ct', channel: 'Email', preview: 'Re: Lease renewal for next year' },
    { name: 'Marcus Lee', place: 'Maple Ave, Unit B', channel: 'Chat', preview: 'Sending the rest of rent Friday.' },
  ];
  return (
    <Panel title="Inbox" meta="All properties">
      <ul className={DIVIDED}>
        {threads.map((t) => (
          <li key={t.name} className="flex items-start gap-3 py-2.5">
            <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${t.unread ? 'bg-[var(--landing-accent)] text-white' : 'bg-[var(--landing-surface-alt)] text-[var(--landing-text-secondary)]'}`}>
              {t.name.split(' ').map((n) => n[0]).join('')}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate text-sm ${t.unread ? `font-semibold ${PRIMARY}` : 'text-[var(--landing-text-secondary)]'}`}>{t.name}</span>
                <Pill tone={t.channel === 'Chat' ? 'info' : 'neutral'}>{t.channel}</Pill>
              </div>
              <div className={MUTED}>{t.place}</div>
              <div className="truncate text-xs text-[var(--landing-text-secondary)]">{t.preview}</div>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

const SCENES: Record<string, { label: string; Scene: () => JSX.Element }> = {
  'rent-collection': {
    label: `Illustration: a bank feed where Zelle and Venmo payments are matched to units, with one unit flagged ${dayCount(F.lateDays)} late and a late fee ready to approve.`,
    Scene: RentScene,
  },
  leasing: {
    label: 'Illustration: one listing posted to Craigslist and Facebook Marketplace, a lead pipeline, and the leasing assistant answering a prospect.',
    Scene: LeasingScene,
  },
  maintenance: {
    label: 'Illustration: a tenant repair request with photos, and the repair coordinator contacting a plumber and booking the visit.',
    Scene: MaintenanceScene,
  },
  'expense-tracking': {
    label: 'Illustration: a forwarded invoice turned into an expense filed under the right unit with its Schedule E category, keeping the original email.',
    Scene: ExpenseScene,
  },
  'ai-assistants': {
    label: 'Illustration: an AI assistant connected to Resida answering who is behind on rent.',
    Scene: AiAssistantsScene,
  },
  dashboard: {
    label: 'Illustration: a list of items needing attention across all units, sorted by urgency.',
    Scene: DashboardScene,
  },
  'lease-management': {
    label: 'Illustration: the lease wizard on the disclosures step, with a state and city reminder that is not legal advice.',
    Scene: LeaseScene,
  },
  walkthroughs: {
    label: 'Illustration: a deduction report comparing move-in and move-out condition item by item.',
    Scene: WalkthroughScene,
  },
  'document-signing': {
    label: 'Illustration: a lease signed by the tenant and waiting for the landlord signature.',
    Scene: SigningScene,
  },
  'tenant-communication': {
    label: 'Illustration: one inbox with chat and email conversations from tenants across properties.',
    Scene: CommunicationScene,
  },
};

/** The illustrative app scene for a feature page, or nothing if the feature has none. */
export function FeatureScene({ slug }: { slug: string }): JSX.Element | null {
  const scene = SCENES[slug];
  if (!scene) return null;
  const { label, Scene } = scene;
  return (
    <div role="img" aria-label={label} data-testid={`feature-scene-${slug}`} className="select-none">
      <Scene />
    </div>
  );
}
